// FS-035 — step-up 2FA na emissão de chave de API (PCP).
//
// PONTO ÚNICO da copy e da regra PURA do fluxo (padrão FS-032): o rito
// /myuxreview e a ratificação do owner trocam texto AQUI, sem caçar string no
// JSX. A tela (`pages/TokensAPI.jsx` + `components/StepUpDialog.jsx`) consome
// e não repete nenhuma frase.
//
// O QUE O FLUXO É, em uma linha: emitir credencial exige redigitar o TOTP no
// ATO (decisão do owner Q1=B); quem ainda não tem 2FA é cortado JÁ, com
// remediação self-service de ~2min (Q2=c).

// ── Contrato com o backend (v2_proxy.py / step_up.py) ─────────────────────
export const STEP_UP_HEADER = "x-step-up-token";
export const STEP_UP_ACTION_TOKEN_CREATE = "token:create";
export const STEP_UP_ENDPOINT = "/auth/2fa/step-up";

// Os DOIS 403 nomeados. São o `detail` INTEIRO da resposta (o backend não põe
// prosa em volta), então o casamento é por igualdade — nunca por substring,
// que casaria uma mensagem humana qualquer que contivesse a palavra.
export const ERROR_STEP_UP_REQUIRED = "step_up_required";
export const ERROR_TWO_FACTOR_ENROLLMENT_REQUIRED = "two_factor_enrollment_required";

// ── COPY pt-BR — RATIFICADA pelo owner (2026-08-16). Verbatim. ────────────
export const COPY_STEPUP = {
  // C1 — banner persistente no topo da tela, tom warning, não-dispensável.
  bannerBody:
    "A emissão de chaves de API agora exige autenticação em duas etapas (2FA). " +
    "Ative o 2FA no seu perfil para continuar — leva cerca de 2 minutos.",
  bannerCta: "Ativar 2FA no perfil",

  // C2 — modal de bloqueio no clique de "Criar credencial" sem 2FA. O botão
  // continua VISÍVEL de propósito: botão sumido parece bug; bloqueio explicado
  // ensina o caminho. A franqueza sobre reprencher o formulário é deliberada.
  blockTitle: "Ative o 2FA para emitir chaves",
  blockBody:
    "Por segurança, criar chaves de API exige autenticação em duas etapas. " +
    "Ative no seu perfil e volte — ao retornar, é só preencher de novo: " +
    "leva poucos segundos.",
  blockCta: "Ativar 2FA no perfil",
  blockSecondary: "Agora não",

  // C3 — modal de step-up no submit da criação.
  title: "Confirme sua identidade",
  body: "Digite o código do seu aplicativo autenticador para emitir a chave de API.",
  codeLabel: "Código de 6 dígitos",
  confirmCta: "Confirmar e emitir",
  cancelCta: "Cancelar",

  // C4/C5/C6 — erros DENTRO do modal (inline, nunca toast: fechar o modal
  // perderia o formulário que o master acabou de preencher).
  invalidCode: "Código inválido ou expirado. Confira o aplicativo e tente novamente.",
  rateLimited: "Muitas tentativas. Aguarde um minuto e tente de novo.",
  proofExpired: "Sua confirmação expirou. Digite o código atual para concluir.",
};

// ── Leitura dos erros nomeados ────────────────────────────────────────────

/** `detail` da resposta quando for STRING; "" em qualquer outro caso.
 *  O 422 do pydantic manda `detail` como ARRAY — tratá-lo como código daria
 *  "[object Object]" na comparação. */
export function errorCodeOf(err) {
  const detail = err?.response?.data?.detail;
  return typeof detail === "string" ? detail : "";
}

export function isStepUpRequired(err) {
  return err?.response?.status === 403 && errorCodeOf(err) === ERROR_STEP_UP_REQUIRED;
}

export function isTwoFactorEnrollmentRequired(err) {
  return err?.response?.status === 403 &&
    errorCodeOf(err) === ERROR_TWO_FACTOR_ENROLLMENT_REQUIRED;
}

// ── Deep-link `/perfil?retorno=` ──────────────────────────────────────────

// ALLOWLIST de destinos. Não é "qualquer path que comece com /": um destino
// aberto vira redirect controlado por quem monta a URL, e o valor chega da
// query string. Rota nova que queira o retorno entra AQUI, explicitamente.
export const RETURN_PATHS = Object.freeze(["/integracao/tokens"]);

/** Destino interno válido, ou `null`. Nunca devolve URL externa. */
export function safeReturnPath(raw) {
  if (typeof raw !== "string") return null;
  const valor = raw.trim();
  // `//host` (protocol-relative) e `/\host` são absolutos para o browser
  // mesmo começando com "/" — a checagem de barra inicial sozinha não basta.
  if (!valor.startsWith("/") || valor.startsWith("//") || valor.startsWith("/\\")) {
    return null;
  }
  return RETURN_PATHS.includes(valor) ? valor : null;
}

/** URL do perfil com o retorno embutido (ou o perfil puro, se inválido). */
export function perfilDeepLink(destino) {
  const alvo = safeReturnPath(destino);
  return alvo ? `/perfil?retorno=${encodeURIComponent(alvo)}` : "/perfil";
}

// ── Idempotência da tentativa ─────────────────────────────────────────────

/**
 * Chave de idempotência da TENTATIVA de criação, gerada no front.
 *
 * POR QUÊ no front (achado do /myuxreview): no retry do estado 3 (prova
 * expirada/consumida) o master reenvia a MESMA criação com uma prova nova. Se
 * a chave nascesse no proxy, cada chamada teria a sua — e um sucesso-fantasma
 * (a V2 criou, a resposta se perdeu) seguido de retry emitiria DUAS
 * credenciais. Com a chave estável por tentativa, a V2 devolve a mesma criação.
 *
 * Forma casada com `sanitize_idempotency_key` do proxy (v2_proxy.py): sem
 * espaço, sem caractere de controle. O fallback existe porque
 * `crypto.randomUUID` só está disponível em contexto seguro.
 */
export function newIdempotencyKey() {
  const uuid = globalThis.crypto?.randomUUID?.();
  if (uuid) return uuid;
  return `pcp-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

/** Só dígitos, no máximo 6 — o campo do TOTP nunca guarda outra coisa. */
export function normalizeTotpInput(raw) {
  return String(raw ?? "").replace(/\D/g, "").slice(0, 6);
}

export const TOTP_CODE_LENGTH = 6;
